import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { App as CapApp } from '@capacitor/app';
import { IconButton } from './IconButton.js';
import { incrementActiveDialogs, decrementActiveDialogs, isAnyDialogOpen } from './dialogManager.js';

export type DialogSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl';

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  size?: DialogSize;
  closeOnEsc?: boolean;
  closeOnOverlayClick?: boolean;
  'data-testid'?: string;
  titleHeadingLevel?: 'h2' | 'h3';
  fullScreenMobile?: boolean;
  bodyClassName?: string;
}

export interface DialogHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  onClose?: () => void;
  closeTestId?: string;
}

export const DialogHeader: React.FC<DialogHeaderProps> = ({
  children,
  onClose,
  closeTestId = 'dialog-close-button',
  className = '',
  ...props
}) => (
  <div
    className={`flex items-start justify-between gap-3 px-6 pt-5 pb-3 border-b border-base-300/60 ${className}`}
    {...props}
  >
    <div className="min-w-0 flex-1">{children}</div>
    {onClose && (
      <IconButton
        label="بستن"
        icon={<X className="size-4" />}
        variant="ghost"
        size="sm"
        onClick={onClose}
        className="shrink-0 -me-2 -mt-1"
        data-testid={closeTestId}
      />
    )}
  </div>
);
DialogHeader.displayName = 'DialogHeader';

export type DialogBodyProps = React.HTMLAttributes<HTMLDivElement>;

export const DialogBody: React.FC<DialogBodyProps> = ({
  children,
  className = '',
  ...props
}) => (
  <div
    className={`max-h-[70vh] overflow-y-auto px-6 py-4 flex-1 ${className}`}
    {...props}
  >
    {children}
  </div>
);
DialogBody.displayName = 'DialogBody';

export type DialogFooterProps = React.HTMLAttributes<HTMLDivElement>;

export const DialogFooter: React.FC<DialogFooterProps> = ({
  children,
  className = '',
  ...props
}) => (
  <div
    className={`flex items-center justify-start gap-3 p-4 lg:px-6 bg-base-200/90 border-t border-base-300 ${className}`}
    {...props}
  >
    {children}
  </div>
);
DialogFooter.displayName = 'DialogFooter';

interface DialogComponentType extends React.FC<DialogProps> {
  Header: typeof DialogHeader;
  Body: typeof DialogBody;
  Footer: typeof DialogFooter;
}

export const Dialog: DialogComponentType = ({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  closeOnEsc = true,
  closeOnOverlayClick = true,
  'data-testid': dataTestId,
  titleHeadingLevel = 'h2',
  fullScreenMobile = false,
  bodyClassName
}) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousActiveElement = useRef<HTMLElement | null>(null);
  const openedAt = useRef(0);

  // Parents usually pass a fresh onClose on every render. If the setup effect depended on it, a dialog inside a
  // parent that re-renders constantly (the tab viewer during playback re-renders hundreds of times a second)
  // would tear down and rebuild its scroll lock, focus trap and native back-button listener on every render,
  // saturating the main thread and starving audio on phones. Keep the latest values in refs instead.
  const onCloseRef = useRef(onClose);
  const closeOnEscRef = useRef(closeOnEsc);
  useEffect(() => {
    onCloseRef.current = onClose;
    closeOnEscRef.current = closeOnEsc;
  });

  useEffect(() => {
    if (!open) return;

    openedAt.current = Date.now();
    incrementActiveDialogs();
    previousActiveElement.current = document.activeElement as HTMLElement;

    // Lock scrolling while dialog is open (the scrollers are #root and the shell's <main>, see index.css)
    document.documentElement.classList.add('fb-scroll-locked');

    // Focus trap - prioritize autoFocus elements if present
    const dialogElement = dialogRef.current;
    if (dialogElement) {
      const autoFocusEl = dialogElement.querySelector<HTMLElement>('[autofocus], [data-autofocus]');
      if (autoFocusEl) {
        autoFocusEl.focus();
      } else {
        const focusables = dialogElement.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusables.length > 0) {
          focusables[0].focus();
        }
      }
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      // Stacked dialogs (e.g. the custom tuning dialog opened from the import dialog): only the topmost reacts.
      const all = document.querySelectorAll('[role="dialog"]');
      if (dialogElement && all.length > 1 && all[all.length - 1] !== dialogElement.closest('[role="dialog"]')) return;
      if (closeOnEscRef.current && e.key === 'Escape') {
        e.preventDefault();
        onCloseRef.current();
        return;
      }

      if (e.key === 'Tab' && dialogElement) {
        const focusables = dialogElement.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusables.length === 0) return;

        const firstElement = focusables[0];
        const lastElement = focusables[focusables.length - 1];

        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    // Capacitor Android hardware back-button listener
    let removeCapListener: (() => void) | undefined;
    try {
      const listenerPromise = CapApp.addListener('backButton', () => {
        onCloseRef.current();
      });
      listenerPromise
        .then((handle) => {
          removeCapListener = () => handle.remove();
        })
        .catch(() => {});
    } catch {
      // Not on native device, ignore
    }

    return () => {
      decrementActiveDialogs();
      if (!isAnyDialogOpen()) {
        document.documentElement.classList.remove('fb-scroll-locked');
      }
      window.removeEventListener('keydown', handleKeyDown);
      removeCapListener?.();
      previousActiveElement.current?.focus?.();
    };
  }, [open]);

  if (!open) return null;

  const sizeClasses: Record<DialogSize, string> = {
    sm: 'md:max-w-md',
    md: 'md:max-w-lg',
    lg: 'md:max-w-xl',
    xl: 'md:max-w-2xl',
    '2xl': 'md:max-w-[960px]'
  };

  const TitleTag = titleHeadingLevel;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'dialog-title' : undefined}
      data-testid={dataTestId}
      className="fixed inset-0 z-50 flex flex-col justify-end md:justify-center items-center p-0 md:p-4"
    >
      {/* Backdrop */}
      <div
        data-testid={dataTestId ? `${dataTestId}-backdrop` : 'dialog-backdrop'}
        aria-hidden="true"
        className="fixed inset-0 bg-black/70 md:backdrop-blur-sm transition-opacity cursor-pointer"
        onClick={
          closeOnOverlayClick
            ? () => {
                // Ignore the synthetic click from the touch release that opened the dialog (long-press)
                if (Date.now() - openedAt.current < 400) return;
                onCloseRef.current();
              }
            : undefined
        }
      />

      {/* Surface: Bottom Sheet on Mobile (< md), Centered Modal on Desktop (>= md) */}
      <div
        ref={dialogRef}
        data-testid={dataTestId ? `${dataTestId}-surface` : undefined}
        className={`relative w-full ${sizeClasses[size]} bg-base-200 border-base-300 shadow-2xl z-10 flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200 ${
          fullScreenMobile
            ? 'h-[100dvh] max-h-none rounded-none border-0 md:h-auto md:max-h-[85vh] md:rounded-3xl md:border'
            : 'border-t md:border rounded-t-3xl md:rounded-3xl max-h-[90vh] md:max-h-[85vh]'
        }`}
      >
        {/* Mobile Drag Handle */}
        {!fullScreenMobile && (
          <div className="flex md:hidden justify-center pt-3 pb-1 shrink-0">
            <div className="w-10 h-1 rounded-full bg-base-content/20" />
          </div>
        )}

        {/* Header Slot or Subcomponent */}
        {title ? (
          <DialogHeader onClose={onClose}>
            <TitleTag id="dialog-title" className="text-lg font-bold text-base-content">
              {title}
            </TitleTag>
            {description && (
              <p className="text-xs lg:text-[13px] text-base-content/60 mt-1">{description}</p>
            )}
          </DialogHeader>
        ) : null}

        {/* Body */}
        {title ? (
          <DialogBody
            className={`${fullScreenMobile ? 'max-md:max-h-none max-md:flex-1' : ''} ${bodyClassName || ''}`.trim()}
          >
            {children}
          </DialogBody>
        ) : (
          children
        )}

        {/* Footer */}
        {footer ? (
          <DialogFooter
            className={fullScreenMobile ? 'shrink-0 max-md:sticky max-md:bottom-0' : ''}
          >
            {footer}
          </DialogFooter>
        ) : null}
      </div>
    </div>
  );
};

Dialog.displayName = 'Dialog';
Dialog.Header = DialogHeader;
Dialog.Body = DialogBody;
Dialog.Footer = DialogFooter;
